// src/modules/PackageManager/CurrentPackagesTab.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronDown, ChevronUp, Copy, Loader2, Pencil, Search, Upload, X } from 'lucide-react';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import {
  fetchDefatomManagedPackages,
  fetchPackageVersionsWithInstallers,
  updatePackageName,
  updateManifests,
  uploadDiscoveryScript,
  uploadUpdateScript,
  ManagedPackageItem,
  PackageVersionDetail,
  formatDate,
  isInstallerDownloaded,
  getDownloadStatusLabel,
  getArchitectureLabel,
  getInstallerTypeLabel,
} from '../../components/services/InstallerManifestService';
import UploadDiscoveryScriptModal from './UploadDiscoveryScriptModal';
import UploadUpdateScriptModal from './UploadUpdateScriptModal';

const ITEMS_PER_PAGE = 20;

const CurrentPackagesTab: React.FC = () => {
  const { addToast } = useToast();

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // Left panel state
  const [packages, setPackages] = useState<ManagedPackageItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [hasMorePackages, setHasMorePackages] = useState(true);

  // Right panel state
  const [selectedPackage, setSelectedPackage] = useState<ManagedPackageItem | null>(null);
  const [versions, setVersions] = useState<PackageVersionDetail[]>([]);
  const [discoveryScriptUrl, setDiscoveryScriptUrl] = useState<string | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPackageName, setEditPackageName] = useState('');

  // Filter state: only show installers with external URL (no blob)
  const [showOnlyExternal, setShowOnlyExternal] = useState(false);

  // Manifest update state
  const [selectedForManifestUpdate, setSelectedForManifestUpdate] = useState<Set<string>>(new Set());
  const [isManifestModalOpen, setIsManifestModalOpen] = useState(false);
  const [isUpdatingManifests, setIsUpdatingManifests] = useState(false);

  // Scroll container ref for lazy loading
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Upload state
  const [isDiscoveryUploadModalOpen, setIsDiscoveryUploadModalOpen] = useState(false);
  const [isUploadingDiscovery, setIsUploadingDiscovery] = useState(false);

  const [isUpdateUploadModalOpen, setIsUpdateUploadModalOpen] = useState(false);
  const [updateUploadVersion, setUpdateUploadVersion] = useState<PackageVersionDetail | null>(null);
  const [isUploadingUpdate, setIsUploadingUpdate] = useState(false);

  // Fetch managed packages
  const loadPackages = useCallback(
    async (page: number, append = false, searchQuery = '') => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoadingPackages(true);

        const response = await fetchDefatomManagedPackages({
          pageNumber: page,
          pageSize: ITEMS_PER_PAGE,
          search: searchQuery || undefined,
        });

        if (response.isSuccess) {
          const newPackages = response.data;

          setPackages((prev) => (append ? [...prev, ...newPackages] : newPackages));
          setTotalPages(response.pagination.totalPages);
          setHasMorePackages(response.pagination.hasNextPage);

          // Auto-select first package if none selected
          if (!selectedPackage && newPackages.length > 0 && !append) {
            handleSelectPackage(newPackages[0]);
          } else if (!append && newPackages.length === 0) {
            setSelectedPackage(null);
          }
        } else {
          addToast(response.message || 'Failed to load packages', 'error');
        }
      } catch (error) {
        console.error('Error loading packages:', error);
        addToast('Failed to load packages', 'error');
      } finally {
        setLoadingPackages(false);
        loadingRef.current = false;
      }
    },
    [selectedPackage, addToast],
  );

  // Fetch package versions with installers
  const loadVersions = useCallback(
    async (packageIdentifier: string) => {
      try {
        setLoadingVersions(true);
        setVersions([]);
        setDiscoveryScriptUrl(null);
        setExpandedVersions(new Set());

        const response = await fetchPackageVersionsWithInstallers(packageIdentifier);

        if (response.isSuccess) {
          const versionsData = response.data?.versions ?? [];
          if (!versionsData.length) {
            setVersions([]);
            addToast(response.message || 'No versions available', 'info');
            return;
          }
          setVersions(versionsData);
          setDiscoveryScriptUrl(response.data?.discoveryScriptUrl || null);
        } else {
          addToast(response.message || 'Failed to load package versions', 'error');
        }
      } catch (error) {
        console.error('Error loading versions:', error);
        addToast('Failed to load package versions', 'error');
      } finally {
        setLoadingVersions(false);
      }
    },
    [addToast],
  );

  // Initial load
  useEffect(() => {
    loadPackages(1, false, activeSearch);
  }, [activeSearch]);

  // Lazy loading scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingPackages || !hasMorePackages) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled 80% down
    if (scrollPercentage > 0.8) {
      const nextPage = currentPage + 1;
      if (nextPage <= totalPages) {
        setCurrentPage(nextPage);
        loadPackages(nextPage, true, activeSearch);
      }
    }
  }, [loadingPackages, hasMorePackages, currentPage, totalPages, loadPackages, activeSearch]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleSelectPackage = (pkg: ManagedPackageItem) => {
    setSelectedPackage(pkg);
    setEditPackageName(pkg.packageName);
    loadVersions(pkg.packageIdentifier);
  };

  const handleSearchSubmit = () => {
    const trimmed = searchInput.trim();
    setActiveSearch(trimmed);
    setCurrentPage(1);
    setPackages([]);
    setSelectedPackage(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const toggleVersionExpand = (versionKey: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(versionKey)) {
        next.delete(versionKey);
      } else {
        next.add(versionKey);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    addToast(`${label} copied to clipboard`, 'success');
  };

  const toggleManifestSelection = (packageIdentifier: string) => {
    setSelectedForManifestUpdate((prev) => {
      const next = new Set(prev);
      if (next.has(packageIdentifier)) {
        next.delete(packageIdentifier);
      } else {
        next.add(packageIdentifier);
      }
      return next;
    });
  };

  const clearManifestSelection = () => {
    setSelectedForManifestUpdate(new Set());
  };

  const openManifestModal = () => {
    setIsManifestModalOpen(true);
  };

  const closeManifestModal = () => {
    if (isUpdatingManifests) return;
    setIsManifestModalOpen(false);
  };

  const handleConfirmUpdateManifests = async () => {
    const appIds = selectedForManifestUpdate.size > 0 ? Array.from(selectedForManifestUpdate) : null;

    try {
      setIsUpdatingManifests(true);
      const response = await updateManifests(appIds);

      if (response.isSuccess) {
        addToast(response.message || 'Manifests updated successfully', 'success');
        setSelectedForManifestUpdate(new Set());
        setIsManifestModalOpen(false);
      } else {
        addToast(response.message || 'Failed to update manifests', 'error');
      }
    } catch (error) {
      console.error('Error updating manifests:', error);
      addToast('Failed to update manifests', 'error');
    } finally {
      setIsUpdatingManifests(false);
    }
  };

  const openEditModal = () => {
    if (!selectedPackage) return;
    setEditPackageName(selectedPackage.packageName);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedPackage) return;

    const trimmed = editPackageName.trim();
    if (!trimmed) {
      addToast('Package name cannot be empty', 'error');
      return;
    }

    if (trimmed === selectedPackage.packageName) {
      addToast('Package name is unchanged', 'info');
      setIsEditModalOpen(false);
      return;
    }

    try {
      setIsEditModalOpen(false); // Close modal immediately for UX

      const response = await updatePackageName(selectedPackage.packageIdentifier, trimmed);

      if (response.isSuccess) {
        // Update local state with the new package name
        setPackages((prev) => prev.map((p) => (p.id === selectedPackage.id ? { ...p, packageName: trimmed } : p)));
        setSelectedPackage((prev) => (prev ? { ...prev, packageName: trimmed } : prev));

        addToast(`Package name updated successfully`, 'success');
      } else {
        // addToast(response.message || 'Failed to update package name', 'error');
        setIsEditModalOpen(true); // Reopen modal on error
      }
    } catch (error) {
      console.error('Error updating package name:', error);
      //   addToast('Failed to update package name', 'error');
      setIsEditModalOpen(true); // Reopen modal on error
    }
  };

  const openDiscoveryUploadModal = () => {
    if (!selectedPackage) return;
    setIsDiscoveryUploadModalOpen(true);
  };

  const closeDiscoveryUploadModal = () => {
    if (isUploadingDiscovery) return;
    setIsDiscoveryUploadModalOpen(false);
  };

  const handleDiscoveryUpload = async (file: File) => {
    if (!selectedPackage) return;

    try {
      setIsUploadingDiscovery(true);
      const response = await uploadDiscoveryScript(selectedPackage.packageIdentifier, file);

      if (response.isSuccess) {
        addToast('Discovery script uploaded successfully', 'success');
        setIsDiscoveryUploadModalOpen(false);
      } else {
        addToast(response.message || 'Failed to upload discovery script', 'error');
      }
    } catch (error) {
      console.error('Error uploading discovery script:', error);
      addToast('Failed to upload discovery script', 'error');
    } finally {
      setIsUploadingDiscovery(false);
    }
  };

  const openUpdateUploadModal = (version: PackageVersionDetail) => {
    setUpdateUploadVersion(version);
    setIsUpdateUploadModalOpen(true);
  };

  const closeUpdateUploadModal = () => {
    if (isUploadingUpdate) return;
    setIsUpdateUploadModalOpen(false);
    setUpdateUploadVersion(null);
  };

  const handleUpdateUpload = async (file: File) => {
    if (!selectedPackage || !updateUploadVersion) return;

    try {
      setIsUploadingUpdate(true);
      const response = await uploadUpdateScript(selectedPackage.packageIdentifier, updateUploadVersion.packageVersion, file);

      if (response.isSuccess) {
        addToast('Update script uploaded successfully', 'success');
        setIsUpdateUploadModalOpen(false);
        setUpdateUploadVersion(null);
      } else {
        addToast(response.message || 'Failed to upload update script', 'error');
      }
    } catch (error) {
      console.error('Error uploading update script:', error);
      addToast('Failed to upload update script', 'error');
    } finally {
      setIsUploadingUpdate(false);
    }
  };

  return (
    <div className="h-full max-h-lvh flex flex-col gap-4">
      {/* Search Bar */}
      <div className="bg-[#1C1C1D] rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search packages by name or identifier (press Enter to search)"
              className="w-full bg-[#1D1D1D] border border-gray-600 rounded pl-10 pr-3 py-2 text-sm text-gray-300 outline-none focus:border-red-500 transition-colors"
            />
          </div>
          <button
            onClick={handleSearchSubmit}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors"
          >
            Search
          </button>
          <button
            onClick={openManifestModal}
            className="px-4 py-2 bg-[#2A2A2B] hover:bg-[#333333] border border-gray-600 text-white text-sm font-medium rounded transition-colors whitespace-nowrap"
          >
            Update Manifests{selectedForManifestUpdate.size > 0 ? ` (${selectedForManifestUpdate.size})` : ''}
          </button>
        </div>
        {(activeSearch || selectedForManifestUpdate.size > 0) && (
          <div className="flex items-center justify-between mt-2">
            {activeSearch ? (
              <p className="text-xs text-gray-400">
                Searching for: <span className="text-red-400">"{activeSearch}"</span>
              </p>
            ) : (
              <span />
            )}
            {selectedForManifestUpdate.size > 0 && (
              <p className="text-xs text-gray-400">
                {selectedForManifestUpdate.size} package{selectedForManifestUpdate.size !== 1 ? 's' : ''} selected{' '}
                <button onClick={clearManifestSelection} className="text-red-400 hover:text-red-300 underline transition-colors">
                  Clear selection
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Panel - Package List */}
        <div className="w-1/3 bg-[#1C1C1D] rounded-lg border border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Managed Packages</h2>
            <p className="text-xs text-gray-500 mt-1">
              {packages.length} package{packages.length !== 1 ? 's' : ''} loaded
            </p>
          </div>

          {/* Package List */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            {loadingPackages && packages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-red-500" size={32} />
              </div>
            ) : packages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-gray-500">{activeSearch ? `No packages found for "${activeSearch}"` : 'No packages found'}</span>
              </div>
            ) : (
              <div className="space-y-1 p-3">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                    className={`p-3 rounded cursor-pointer transition-colors flex items-start gap-2 ${
                      selectedPackage?.packageIdentifier === pkg.packageIdentifier
                        ? 'bg-red-600/20 border border-red-500/50'
                        : 'bg-[#2A2A2B] hover:bg-[#333333] border border-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedForManifestUpdate.has(pkg.packageIdentifier)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleManifestSelection(pkg.packageIdentifier)}
                      className="mt-1 shrink-0 accent-red-600"
                      title="Select for manifest update"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{pkg.packageName}</p>
                      <p className="text-xs text-gray-400 truncate">{pkg.packageIdentifier}</p>
                      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                        <span>v{pkg.packageVersion}</span>
                        <span>{formatDate(pkg.lastUpdated)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Lazy loading indicator */}
                {loadingPackages && packages.length > 0 && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="animate-spin text-red-500" size={20} />
                    <span className="ml-2 text-xs text-gray-400">Loading more...</span>
                  </div>
                )}

                {!hasMorePackages && packages.length > 0 && (
                  <div className="text-center py-4 text-xs text-gray-500">All packages loaded</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Version Details */}
        <div className="flex-1 bg-[#1C1C1D] rounded-lg border border-gray-700 flex flex-col">
          {selectedPackage ? (
            <>
              <div className="border-b border-gray-700">
                <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-white truncate">{selectedPackage.packageName}</h2>
                    <p className="text-xs text-gray-400 mt-1 truncate">{selectedPackage.packageIdentifier}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                    <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer px-1 py-1.5">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={showOnlyExternal}
                          onChange={(e) => setShowOnlyExternal(e.target.checked)}
                        />
                        <div
                          className={`w-8 h-4 rounded-full transition-colors duration-200 ${showOnlyExternal ? 'bg-red-600' : 'bg-gray-600'}`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full bg-white transition-transform duration-200 transform ${
                              showOnlyExternal ? 'translate-x-4 translate-y-0.5' : 'translate-x-0.5 translate-y-0.5'
                            }`}
                          />
                        </div>
                      </div>
                      Only external installers
                    </label>
                    <button
                      onClick={openDiscoveryUploadModal}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors whitespace-nowrap"
                      title="Upload Discovery Script"
                    >
                      <Upload size={14} />
                      Upload Discovery
                    </button>
                    <button
                      onClick={openEditModal}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors whitespace-nowrap"
                      title="Edit Package Name"
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                  </div>
                </div>

                {discoveryScriptUrl && (
                  <div className="px-4 pb-4">
                    <label className="block text-xs font-medium text-gray-300 mb-1">Discovery Script</label>
                    <div className="flex items-start gap-2">
                      <textarea
                        value={discoveryScriptUrl}
                        readOnly
                        rows={2}
                        wrap="soft"
                        spellCheck={false}
                        className="flex-1 bg-[#1D1D1D] border border-gray-600 rounded px-3 py-2 text-xs text-gray-300 outline-none resize-y leading-snug"
                      />
                      <button
                        onClick={() => copyToClipboard(discoveryScriptUrl, 'Discovery Script URL')}
                        className="p-2 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                        title="Copy"
                      >
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar p-4">
                {loadingVersions ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="animate-spin text-red-500" size={32} />
                  </div>
                ) : versions.length > 0 ? (
                  <div className="space-y-2">
                    {versions.map((version, idx) => {
                      // Apply filter only when toggle is checked
                      const installersFiltered = showOnlyExternal
                        ? version.installers.filter((inst) => inst.installerUrl && !inst.installerBlobLocation)
                        : version.installers;

                      // Skip version if no installers match the filter
                      if (installersFiltered.length === 0) return null;

                      const versionKey = `${version.packageVersion}-${idx}`;
                      const isExpanded = expandedVersions.has(versionKey);

                      return (
                        <div key={versionKey} className="bg-[#2A2A2B] rounded border border-gray-700">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleVersionExpand(versionKey)}
                              className="flex-1 min-w-0 p-3 flex items-center gap-3 hover:bg-[#333333] transition-colors text-left rounded"
                            >
                              {isExpanded ? (
                                <ChevronUp size={16} className="text-red-500 flex-shrink-0" />
                              ) : (
                                <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-sm font-medium text-white truncate">v{version.packageVersion}</p>
                                  <span className="text-xs text-gray-500 flex-shrink-0">{version.installers.length} installer(s)</span>
                                </div>
                                <p className="text-xs text-gray-400">{formatDate(version.updatedAt)}</p>
                              </div>
                            </button>
                            <div className="pr-3 flex-shrink-0">
                              <button
                                onClick={() => openUpdateUploadModal(version)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors whitespace-nowrap"
                                title="Upload Update Script"
                              >
                                <Upload size={12} />
                                Upload Script
                              </button>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="border-t border-gray-700 p-3 bg-[#1C1D1D] space-y-4">
                              {version.updateScriptUrl && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-300 mb-1">Update Script URL</label>
                                  <div className="flex items-start gap-2">
                                    <textarea
                                      value={version.updateScriptUrl}
                                      readOnly
                                      rows={2}
                                      wrap="soft"
                                      spellCheck={false}
                                      className="flex-1 bg-[#1D1D1D] border border-gray-600 rounded px-3 py-2 text-xs text-gray-300 outline-none resize-y leading-snug"
                                    />
                                    <button
                                      onClick={() => copyToClipboard(version.updateScriptUrl || '', 'Update Script URL')}
                                      className="p-2 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                                      title="Copy"
                                    >
                                      <Copy size={14} className="text-gray-400" />
                                    </button>
                                  </div>
                                </div>
                              )}
                              {Object.entries(
                                installersFiltered.reduce<Record<string, typeof installersFiltered>>((acc, inst) => {
                                  const key = (inst.architecture || 'unknown').toLowerCase();
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(inst);
                                  return acc;
                                }, {}),
                              ).map(([archKey, archInstallers]) => {
                                const archLabel = getArchitectureLabel(archKey);

                                return (
                                  <div key={archKey} className="rounded border border-gray-700">
                                    {/* Architecture header */}
                                    <div className="px-3 py-2 bg-[#232324] border-b border-gray-700 flex items-center justify-between">
                                      <p className="text-xs font-semibold text-gray-200">{archLabel}</p>
                                      <span className="text-[10px] text-gray-500">{archInstallers.length} installer(s)</span>
                                    </div>

                                    <div className="p-3 space-y-3">
                                      {archInstallers.map((installer, idx2) => {
                                        const isDownloaded = isInstallerDownloaded(installer);
                                        const typeLabel = getInstallerTypeLabel(installer.installerType);

                                        const installerUrl = installer.installerUrl || '';
                                        const blobUrl = installer.installerBlobLocation || '';

                                        const installerRows = installerUrl
                                          ? Math.min(6, Math.max(2, Math.ceil(installerUrl.length / 60)))
                                          : 2;
                                        const blobRows = blobUrl ? Math.min(6, Math.max(2, Math.ceil(blobUrl.length / 60))) : 2;

                                        return (
                                          <div
                                            key={`${archKey}-${idx2}`}
                                            className="bg-[#2A2A2B] rounded border border-gray-600 p-3 space-y-3"
                                          >
                                            {/* Installer metadata */}
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="text-gray-300 font-medium">
                                                {typeLabel !== 'Unknown' ? `${archLabel} - ${typeLabel}` : archLabel}
                                              </span>
                                              <span className={isDownloaded ? 'text-green-400' : 'text-yellow-400'}>
                                                {getDownloadStatusLabel(installer)}
                                              </span>
                                            </div>

                                            {/* Installer URL (external) */}
                                            {installerUrl && (
                                              <div>
                                                <label className="block text-xs font-medium text-gray-300 mb-1">Installer URL</label>
                                                <div className="flex items-start gap-2">
                                                  <textarea
                                                    value={installerUrl}
                                                    readOnly
                                                    rows={installerRows}
                                                    wrap="soft"
                                                    spellCheck={false}
                                                    className="flex-1 bg-[#1D1D1D] border border-gray-600 rounded px-3 py-2 text-xs text-gray-300 outline-none resize-y leading-snug"
                                                  />
                                                  <div className="flex flex-col gap-2">
                                                    <button
                                                      onClick={() => copyToClipboard(installerUrl, 'Installer URL')}
                                                      className="p-2 hover:bg-gray-700 rounded transition-colors"
                                                      title="Copy"
                                                    >
                                                      <Copy size={14} className="text-gray-400" />
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Blob URL (if available) */}
                                            {blobUrl && (
                                              <div>
                                                <label className="block text-xs font-medium text-gray-300 mb-1">Blob URL</label>
                                                <div className="flex items-start gap-2">
                                                  <textarea
                                                    value={blobUrl}
                                                    readOnly
                                                    rows={blobRows}
                                                    wrap="soft"
                                                    spellCheck={false}
                                                    className="flex-1 bg-[#1D1D1D] border border-gray-600 rounded px-3 py-2 text-xs text-gray-300 outline-none resize-y leading-snug"
                                                  />
                                                  <div className="flex flex-col gap-2">
                                                    <button
                                                      onClick={() => copyToClipboard(blobUrl, 'Blob URL')}
                                                      className="p-2 hover:bg-gray-700 rounded transition-colors"
                                                      title="Copy"
                                                    >
                                                      <Copy size={14} className="text-gray-400" />
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-gray-500 text-sm">No versions available</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <span className="text-gray-500">Select a package to view details</span>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeEditModal} />
          <div className="relative z-10 w-full max-w-md bg-[#1C1C1D] border border-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white">Edit Package Name</h3>
                <p className="text-xs text-gray-400 mt-1 truncate">Identifier: {selectedPackage?.packageIdentifier}</p>
              </div>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-white transition-colors flex-shrink-0" title="Close">
                <X size={16} />
              </button>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-300 mb-1">Package Name</label>
              <input
                type="text"
                value={editPackageName}
                onChange={(e) => setEditPackageName(e.target.value)}
                className="w-full bg-[#1D1D1D] border border-gray-600 rounded px-3 py-2 text-xs text-gray-300 outline-none"
                placeholder="Enter package name"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeEditModal}
                className="px-3 py-1.5 rounded border border-gray-700 text-xs text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Manifests Confirmation Modal */}
      {isManifestModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeManifestModal} />
          <div className="relative z-10 w-full max-w-md bg-[#1C1C1D] border border-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold text-white">Update Manifests</h3>
              <button
                onClick={closeManifestModal}
                disabled={isUpdatingManifests}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0 disabled:opacity-50"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {selectedForManifestUpdate.size > 0 ? (
              <>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400">
                    This will update the manifest for {selectedForManifestUpdate.size} selected package
                    {selectedForManifestUpdate.size !== 1 ? 's' : ''}:
                  </p>
                  <button
                    onClick={clearManifestSelection}
                    disabled={isUpdatingManifests}
                    className="text-xs text-red-400 hover:text-red-300 underline transition-colors disabled:opacity-50 whitespace-nowrap ml-2"
                  >
                    Clear all
                  </button>
                </div>
                <div className="mt-2 max-h-40 overflow-y-auto bg-[#1D1D1D] border border-gray-700 rounded p-2 space-y-1">
                  {Array.from(selectedForManifestUpdate).map((identifier) => (
                    <p key={identifier} className="text-xs text-gray-300 truncate">
                      {identifier}
                    </p>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400 mt-2">
                No packages are selected, so this will update the manifests for{' '}
                <span className="text-yellow-400 font-medium">all packages</span>. This may take a while. Continue?
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeManifestModal}
                disabled={isUpdatingManifests}
                className="px-3 py-1.5 rounded border border-gray-700 text-xs text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpdateManifests}
                disabled={isUpdatingManifests}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors disabled:opacity-50"
              >
                {isUpdatingManifests && <Loader2 className="animate-spin" size={14} />}
                {isUpdatingManifests ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discovery Script Upload Modal */}
      <UploadDiscoveryScriptModal
        show={isDiscoveryUploadModalOpen}
        packageName={selectedPackage?.packageName || null}
        packageIdentifier={selectedPackage?.packageIdentifier || null}
        uploading={isUploadingDiscovery}
        onClose={closeDiscoveryUploadModal}
        onUpload={handleDiscoveryUpload}
      />

      {/* Update Script Upload Modal */}
      <UploadUpdateScriptModal
        show={isUpdateUploadModalOpen}
        packageName={selectedPackage?.packageName || null}
        packageIdentifier={selectedPackage?.packageIdentifier || null}
        packageVersion={updateUploadVersion?.packageVersion || null}
        uploading={isUploadingUpdate}
        onClose={closeUpdateUploadModal}
        onUpload={handleUpdateUpload}
      />
    </div>
  );
};

export default CurrentPackagesTab;
